import React from "react";
import styled from "@emotion/styled";
import { LeaderboardTable } from "./LeaderboardTable";
import { LeaderboardCard, Props as LbCardData } from "./LeaderboardCard";
import { Icon } from "../../Icon";
// import { Head } from "components/Head";

import { LeaderboardVariant } from "../../LeaderboardVariant";
import { getShieldIconUrl } from "../utils/get-shield-icon-url";
import { getOtherLeaderboards } from "../utils/get-other-leaderboards";

import styles from "../styles/Leaderboard.module.scss";

const LeaderboardVariantConfigMap = {
  [LeaderboardVariant.Members]: {
    title: "Members",
    subtitle: "Tree planting leaderboard",
    tagline: [
      `Our fantastic community makes it all possible.`,
      `The individuals below have single-handedly managed to support the planting of more trees than any other members on our platform!`,
      `Can you get into the top 10?`,
    ],
  },
  [LeaderboardVariant.Business]: {
    title: "Businesses",
    subtitle: "Tree planting leaderboard",
    tagline: [
      `The businesses ranked below have supported the planting of the most trees through our reforestation projects.`,
      `Can your business be in the top 10?`,
    ],
  },
  [LeaderboardVariant.Referral]: {
    title: "Referrers",
    subtitle: "Leaderboard",
    tagline: [
      `Our sparkly trees are awarded to members who refer others to use Ecologi.`,
      `The more you refer, the more beautiful sparkly trees you get in your profile!`,
      `Can you get into the top 10?`,
    ],
  },
};

export type LeaderboardBaseProps = {
  variant: LeaderboardVariant;
  url: string;
};

type Props = {
  top10Rankings: any;
  top100Rankings: any;
} & LeaderboardBaseProps;

const LeaderboardHero = styled.div`
  background-color: ${(props) => props.theme.colors.charcoal};
  color: #e8e4df;
  padding: 100px 10px 120px 10px;
  width: 100%;
`;

const LeaderboardHeroContent = ({
  title,
  tagline,
  subtitle,
}: {
  title: string;
  tagline: string[];
  subtitle: string;
}) => (
  <section>
    <div className={styles.contentContainer}>
      <div
        style={{
          backgroundImage: `url("${getShieldIconUrl(title)}")`,
        }}
        className={styles.heroSectionShield}
      />
      <h2 className={styles.heroSectionTitleP1}>{title}</h2>
      <h2 className={styles.heroSectionTitleP2}>{subtitle}</h2>
      {tagline.map((line, idx) => (
        <h3 key={idx} className={styles.heroSectionTagline}>
          {line}
        </h3>
      ))}
    </div>
  </section>
);

const OtherLeaderboardsSection = ({
  otherLbData,
}: {
  otherLbData: LbCardData[];
}) => {
  return (
    <section className={styles.moreLbSection}>
      <div className={styles.contentContainer}>
        <h3 className={styles.moreLbSectionTitle}>More leaderboards</h3>
        <div className={styles.moreLbSectionCards}>
          {otherLbData.map((lbData, idx: number) => (
            <div className={styles.moreLbSectionCardWrapper} key={idx}>
              <LeaderboardCard {...lbData} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const LeaderboardLoading = ({ url, variant }: LeaderboardBaseProps) => {
  const { title, tagline, subtitle } = LeaderboardVariantConfigMap[variant];
  const otherLbData = getOtherLeaderboards(url);

  return (
    <div>
      <LeaderboardHero>
        <LeaderboardHeroContent
          title={title}
          tagline={tagline}
          subtitle={subtitle}
        />
        <Icon isCenter type="loading" />
      </LeaderboardHero>

      <OtherLeaderboardsSection otherLbData={otherLbData} />
    </div>
  );
};

export const Leaderboard = ({
  top10Rankings,
  top100Rankings,
  variant,
  url,
}: Props) => {
  const { title, tagline, subtitle } = LeaderboardVariantConfigMap[variant];
  const otherLbData = getOtherLeaderboards(url);

  return (
    <div>
      {/* <Head
        title={`${title} Leaderboard`}
        description={tagline}
        imageUrl={socialImgUrl}
      /> */}
      <LeaderboardHero>
        <LeaderboardHeroContent
          title={title}
          tagline={tagline}
          subtitle={subtitle}
        />
      </LeaderboardHero>

      <section className={styles.tablesSection}>
        <div className={styles.contentContainer}>
          <div className={styles.tableWrapper}>
            <div className={styles.tableRefreshInfo}>Updated once an hour</div>
            <LeaderboardTable
              userRankings={top10Rankings}
              pageTitle={title}
              title="Top 10"
            />
          </div>
          <div className={styles.table2Wrapper}>
            <LeaderboardTable
              pageTitle={title}
              userRankings={top100Rankings}
              title="Top 11-100"
              isTwoColumn
            />
          </div>
          {/*<p className={styles.tablesSectionText}>Thanks to everyone!</p>
          <div className={styles.tablesSectionEarthIcon} />*/}
        </div>
      </section>

      <OtherLeaderboardsSection otherLbData={otherLbData} />
      {/* <SignupCTA /> */}
    </div>
  );
};

Leaderboard.Loading = LeaderboardLoading;
Leaderboard.LeaderboardVariant = LeaderboardVariant;

export default Leaderboard;
